import { computed, type Plugin } from 'vue';

import { useProjectStore } from '@/store/project';
import type {
  Bookmark,
  BookmarkData,
  BookmarkRoute,
  BookmarkTab,
  CreateBookmarkRequest,
  UpdateBookmarkRequest,
} from '@teemill/projects';
import { watchImmediate } from '@vueuse/core';
import { isEqual, pick } from 'lodash';
import { defineStore } from 'pinia';
import type {
  RouteLocation,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationResolvedGeneric,
} from 'vue-router';
import { useAuth } from './auth';
import { useProjects } from './projects';

type BookmarkableRoute = RouteLocationResolvedGeneric | RouteLocationNormalizedLoadedGeneric;

interface BookmarksState {
  projectBookmarks: Record<string, Bookmark[]>;
  projectBookmarkTabs: Record<string, BookmarkTab[]>;
}

export const useBookmarksStore = defineStore('bookmarks', {
  state: (): BookmarksState => ({
    projectBookmarks: {},
    projectBookmarkTabs: {},
  }),

  getters: {
    getProjectBookmarks(): (projectId: string) => Bookmark[] {
      return (projectId: string) => this.projectBookmarks[projectId] || [];
    },
    getProjectBookmarkTabs(): (projectId: string) => BookmarkTab[] {
      return (projectId: string) => this.projectBookmarkTabs[projectId] || [];
    },
  },

  actions: {
    setProjectBookmarks(projectId: string, bookmarks: Bookmark[]) {
      this.projectBookmarks[projectId] = bookmarks;
    },

    addProjectBookmark(projectId: string, bookmark: Bookmark) {
      if (!this.projectBookmarks[projectId]) {
        this.projectBookmarks[projectId] = [];
      }

      this.projectBookmarks[projectId].push(bookmark);
    },

    updateProjectBookmark(projectId: string, bookmark: Bookmark) {
      this.projectBookmarks[projectId] = this.projectBookmarks[projectId].map((b) =>
        b.id === bookmark.id ? bookmark : b,
      );
    },

    removeProjectBookmark(projectId: string, bookmarkId: string) {
      const bookmarks = this.projectBookmarks[projectId];
      if (!bookmarks) return;

      this.projectBookmarks[projectId] = bookmarks.filter((b) => b.id !== bookmarkId);
    },
    setProjectBookmarkTabs(projectId: string, tabs: BookmarkTab[]) {
      this.projectBookmarkTabs[projectId] = tabs;
    },
  },
});

const simplifyRoute = (route: RouteLocation) => pick(route, ['name', 'params', 'query']);

const isBookmarkRoute = (data: BookmarkData): data is BookmarkRoute => {
  return 'route' in data;
};

const routeMatch = ({ data }: Bookmark, route: BookmarkableRoute) => {
  return isBookmarkRoute(data) && isEqual(data.route, simplifyRoute(route));
};

export const useBookmarks = () => {
  const store = useBookmarksStore();
  const projectStore = useProjectStore();
  const { bookmarks: bookmarksClient, bookmarkTabs: tabClient } = useProjects();

  const getBookmarks = computed(() => {
    const projectId = projectStore.active?.id;
    if (!projectId) return [];

    return store.getProjectBookmarks(projectId);
  });

  const saveBookmark = (bookmarkable: CreateBookmarkRequest | BookmarkableRoute) => {
    const projectId = projectStore.active?.id;
    if (!projectId) {
      return Promise.reject(new Error('No project selected'));
    }

    if ('name' in bookmarkable) {
      const simplifiedRoute = simplifyRoute(bookmarkable);
      bookmarkable = {
        data: {
          route: {
            name: simplifiedRoute.name as string,
            params: simplifiedRoute.params,
            query: simplifiedRoute.query,
          },
        },
      };
    }

    return bookmarksClient.create(projectId, bookmarkable).then(() => {
      bookmarksClient
        .list(projectId)
        .then((bookmarks) => {
          store.setProjectBookmarks(projectId, bookmarks);
        })
        .catch((error) => {
          switch (error.response?.status) {
            case 401:
            case 403:
              store.setProjectBookmarks(projectId, []);
              break;
            default:
              throw error;
          }
        });
    });
  };

  const updateBookmark = (bookmarkable: UpdateBookmarkRequest, bookmarkId: string) => {
    const projectId = projectStore.active?.id;
    if (!projectId) {
      return Promise.reject(new Error('No project selected'));
    }

    return bookmarksClient
      .update(projectId, bookmarkId, {
        title: bookmarkable.title,
        data: bookmarkable.data,
        description: bookmarkable.description,
        color: bookmarkable.color,
        icon: bookmarkable.icon,
        tabId: bookmarkable.tabId,
        sortOrder: bookmarkable.sortOrder,
        tabSortOrder: bookmarkable.tabSortOrder,
      })
      .then(() => {
        bookmarksClient
          .list(projectId)
          .then((bookmarks) => {
            store.setProjectBookmarks(projectId, bookmarks);
          })
          .catch((error) => {
            switch (error.response?.status) {
              case 401:
              case 403:
                store.setProjectBookmarks(projectId, []);
                break;
              default:
                throw error;
            }
          });
      });
  };

  const forgetBookmark = (bookmarkId: string) => {
    const projectId = projectStore.active?.id;
    if (!projectId) {
      return Promise.reject(new Error('No project selected'));
    }

    return bookmarksClient.delete(projectId, bookmarkId).then(() => {
      store.removeProjectBookmark(projectId, bookmarkId);
    });
  };

  const getBookmark = (identifier: BookmarkableRoute | string) => {
    if (typeof identifier === 'string') {
      return computed(() => getBookmarks.value.find((b) => b.id === identifier));
    } else {
      return computed(() => getBookmarks.value.find((b) => routeMatch(b, identifier)));
    }
  };

  const hasBookmark = (identifier: BookmarkableRoute | string) => {
    return computed(() => getBookmark(identifier).value !== undefined);
  };

  const getTabs = computed(() => {
    const projectId = projectStore.active?.id;
    if (!projectId) return [];

    return store.getProjectBookmarkTabs(projectId);
  });

  const updateTabs = (tabs: { id: string; name: string; sortOrder: number }[]) => {
    return tabClient
      .update(projectStore.active?.id as string, {
        tabs: tabs.map((item, index) => ({
          id: item.id.startsWith('new-') ? null : item.id,
          name: item.name,
          sortOrder: index + 1,
        })),
      })
      .then((response) => {
        store.setProjectBookmarkTabs(projectStore.active?.id as string, response.tabs);
      });
  };

  return {
    all: getBookmarks,
    tabs: getTabs,
    save: saveBookmark,
    update: updateBookmark,
    forget: forgetBookmark,
    get: getBookmark,
    has: hasBookmark,
    isBookmarkRoute,
    updateTabs: updateTabs,
  };
};

export const bookmarks: Plugin = {
  install: () => {
    const bookmarksStore = useBookmarksStore();
    const projectStore = useProjectStore();
    const { bookmarks: bookmarksClient, bookmarkTabs: tabClient } = useProjects();

    watchImmediate(
      () => projectStore.active,
      (project) => {
        if (project) {
          const auth = useAuth();

          if (auth.isAuthenticatedAs('user')) {
            bookmarksClient
              .list(project.id)
              .then((bookmarks) => {
                bookmarksStore.setProjectBookmarks(project.id, bookmarks);
              })
              .catch((error) => {
                switch (error.response?.status) {
                  case 401:
                  case 403:
                    bookmarksStore.setProjectBookmarks(project.id, []);
                    break;

                  default:
                    throw error;
                }
              });
            tabClient
              .list(project.id)
              .then((response) => {
                bookmarksStore.setProjectBookmarkTabs(project.id, response.data.tabs);
              })
              .catch((error) => {
                switch (error.response?.status) {
                  case 401:
                  case 403:
                    bookmarksStore.setProjectBookmarkTabs(project.id, []);
                    break;

                  default:
                    throw error;
                }
              });
          }
        }
      },
    );
  },
};
